import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../api";

function fmtSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function attMeta(a) {
  const ct = (a.content_type || "").toLowerCase();
  const fn = (a.filename || "").toLowerCase();
  const ext = (fn.split(".").pop() || "").toLowerCase();
  const isImage = ct.startsWith("image") || /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)$/.test(fn);
  const isPdf = ct.includes("pdf") || ext === "pdf";
  let icon = "📄", kind = ext.toUpperCase() || "FILE";
  if (isImage) { icon = "🖼"; kind = "Immagine"; }
  else if (isPdf) { icon = "📕"; kind = "PDF"; }
  else if (/(doc|docx|odt)$/.test(ext)) { icon = "📘"; kind = "Word"; }
  else if (/(xls|xlsx|ods|csv)$/.test(ext)) { icon = "📗"; kind = "Excel"; }
  else if (/(ppt|pptx)$/.test(ext)) { icon = "📙"; kind = "PowerPoint"; }
  else if (/(zip|rar|7z|tar|gz)$/.test(ext)) { icon = "🗜"; kind = "Archivio"; }
  else if (/(xml|p7m)$/.test(ext)) { icon = "🧾"; kind = "Fattura/XML"; }
  return { isImage, isPdf, icon, kind };
}

export default function EmailPreview({ emailId }) {
  const [msg, setMsg] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [blobUrls, setBlobUrls] = useState({});

  const load = useCallback(() => {
    api.get(`/emails/${emailId}`).then((r) => setMsg(r.data)).catch(() => setMsg(null));
    api.get(`/emails/${emailId}/attachments`).then((r) => setAttachments(r.data)).catch(() => setAttachments([]));
  }, [emailId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const created = [];
    attachments.forEach((a) => {
      if (attMeta(a).isImage) {
        api.get(`/emails/${emailId}/attachments/${a.index}?inline=1`, { responseType: "blob" })
          .then((r) => { const u = URL.createObjectURL(r.data); created.push(u); setBlobUrls((prev) => ({ ...prev, [a.index]: u })); })
          .catch(() => {});
      }
    });
    return () => { created.forEach((u) => URL.revokeObjectURL(u)); };
  }, [attachments, emailId]);

  const onFrameLoad = (e) => {
    const f = e.target;
    try {
      const d = f.contentDocument || f.contentWindow.document;
      d.querySelectorAll("a[href]").forEach((a) => { a.target = "_blank"; a.rel = "noopener noreferrer"; });
    } catch (_) {}
    const resize = () => {
      try {
        const doc = f.contentDocument || f.contentWindow.document;
        const h = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
        if (h > 40) f.style.height = (h + 24) + "px";
      } catch (_) {}
    };
    resize(); setTimeout(resize, 350); setTimeout(resize, 1200);
  };

  const openAttachment = async (a) => {
    const w = window.open("", "_blank");
    try {
      const r = await api.get(`/emails/${emailId}/attachments/${a.index}?inline=1`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      if (w) w.location = u; else window.open(u, "_blank");
    } catch (e) { if (w) w.close(); alert("Errore apertura allegato: " + (e?.response?.status === 401 ? "sessione scaduta" : e.message)); }
  };
  const downloadBlob = (data, filename) => {
    const u = URL.createObjectURL(data);
    const link = document.createElement("a");
    link.href = u; link.download = filename || "allegato";
    document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(u), 2000);
  };
  const downloadAttachment = async (a) => {
    try { const r = await api.get(`/emails/${emailId}/attachments/${a.index}`, { responseType: "blob" }); downloadBlob(r.data, a.filename); }
    catch (e) { alert("Errore download allegato: " + e.message); }
  };
  const downloadZip = async () => {
    try { const r = await api.get(`/emails/${emailId}/attachments-zip`, { responseType: "blob" }); downloadBlob(r.data, `allegati_${(msg?.subject || "email").slice(0, 30)}.zip`); }
    catch (e) { alert("Errore download zip: " + e.message); }
  };

  if (!msg) return <div className="muted" style={{ fontSize: 13 }}>Carico la mail collegata…</div>;

  return (
    <div className="email-preview" data-testid="task-email-preview">
      <div className="email-preview-head">
        <div>
          <div className="email-preview-subj">{msg.subject || "(senza oggetto)"}</div>
          <div className="muted" style={{ fontSize: 12.5 }}>
            Da: <strong>{msg.from_name || msg.from_email}</strong>
            {msg.from_name && msg.from_email ? ` <${msg.from_email}>` : ""}
            {msg.date ? " · " + new Date(msg.date).toLocaleString("it-IT") : ""}
          </div>
        </div>
        <Link className="btn sm" to={`/email/${emailId}`} data-testid="task-open-full-email">Apri mail completa →</Link>
      </div>

      <div className="email-preview-body">
        {msg.body_html ? (
          <iframe title="task-email-html" className="email-html-frame" sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            srcDoc={msg.body_html} onLoad={onFrameLoad} data-testid="task-email-body" style={{ maxHeight: 340 }} />
        ) : (
          <div className="email-body" style={{ whiteSpace: "pre-wrap", maxHeight: 300, overflowY: "auto" }}>{msg.body_text || "(nessun testo)"}</div>
        )}
      </div>

      {attachments.length > 0 && (
        <div style={{ marginTop: 12 }} data-testid="task-attachments-panel">
          <div className="toolbar" style={{ marginBottom: 8 }}>
            <strong style={{ fontSize: 14 }}>Allegati ({attachments.length})</strong>
            <div className="spacer" />
            <button className="btn primary sm" onClick={downloadZip} data-testid="task-download-all-zip">⬇ Scarica tutti (zip)</button>
          </div>
          <div className="att-grid">
            {attachments.map((a) => {
              const m = attMeta(a);
              return (
                <div key={a.index} className="att-card" data-testid={`task-attachment-row-${a.index}`}>
                  <div className="att-thumb" onClick={() => openAttachment(a)} style={{ cursor: "pointer" }}>
                    {m.isImage && blobUrls[a.index] ? <img src={blobUrls[a.index]} alt={a.filename} loading="lazy" /> : <span className="att-icon">{m.icon}</span>}
                  </div>
                  <div className="att-info">
                    <div className="att-name" title={a.filename}>{a.filename}</div>
                    <div className="att-meta">{m.kind} · {fmtSize(a.size)}</div>
                    <div className="att-actions">
                      <button className="btn sm" onClick={() => openAttachment(a)}>Anteprima</button>
                      <button className="btn sm" onClick={() => downloadAttachment(a)} data-testid={`task-download-attachment-${a.index}`}>Scarica</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
